import React from 'react';
import isFinite from 'lodash/fp/isFinite';

import Button from 'common/components/Button';
import { TranslatedMessage } from 'common/components/Intl';
import classNames from 'classnames';
import { localisedPath } from 'common/url_locale_helper';
import { LineItem, Variant } from 'apps/cart/types';
import PriceSpan from 'common/components/formatters/PriceSpan';

interface Props {
  lineItemsPending: LineItem[];
  sku?: string;
  isSoldOut?: boolean;
  retailExclusive?: boolean;
  isSubscriberExclusive?: boolean;
  isSubscribed?: boolean;
  offeringId?: number;
  currentCartQuantity?: number;
  useQuantityInput?: boolean;
  hasSubscriptionConflict?: boolean;
  isOpen?: boolean;
  addOfferingWithoutQuantityInput?(params: {
    sku: string;
    offeringId: number;
    quantity: number;
  }): void;
  updateLineItems?(lineItems: LineItem[]): void;
  onClick?(): void;
  buttonComponent?: React.ReactNode;
  className?: string;
  pendingQuantity?: number;
  pendingQuantities?: number;
  price?: number;
  isSaving?: boolean;
  variantsSaving?: Variant[];
  openCartForDesktop?(boolean): void;
  text?: string;
}

export class AddToCartButton extends React.Component<Props> {
  static defaultProps = {
    retailExclusive: false,
    useQuantityInput: true,
  };

  handleAddToCartClick = () => {
    const {
      lineItemsPending,
      offeringId,
      hasSubscriptionConflict,
      isOpen,
      addOfferingWithoutQuantityInput,
      updateLineItems,
      onClick,
      useQuantityInput,
      sku,
      currentCartQuantity,
    } = this.props;
    if (hasSubscriptionConflict) {
      return onClick && onClick();
    }
    const hasOfferingId = !!offeringId;
    if (hasOfferingId) {
      if (!useQuantityInput) {
        addOfferingWithoutQuantityInput({
          sku,
          offeringId,
          quantity: currentCartQuantity + 1,
        });
      } else {
        const item = lineItemsPending.find(
          item => item.offeringId === offeringId
        );

        if (item) {
          item.offeringId = offeringId;
          updateLineItems([item]);
        }
      }
    } else {
      updateLineItems(lineItemsPending);
    }

    if (!isOpen) {
      const shouldAutoClose = hasOfferingId;
      this.props.openCartForDesktop(shouldAutoClose);
    }
    onClick && onClick();
  };

  getDisplayQuantity = (
    pendingQuantity: number,
    pendingQuantities: number,
    offeringId: number
  ) => {
    if (offeringId && pendingQuantities) {
      const lineItem = Array.isArray(pendingQuantities)
        ? pendingQuantities.find(item => item.offeringId === offeringId)
        : pendingQuantities;
      return lineItem ? lineItem.quantity : 0;
    }
    return Array.isArray(pendingQuantities)
      ? pendingQuantities.reduce((total, item) => {
          return total + item.quantity;
        }, 0)
      : pendingQuantity;
  };

  get text() {
    const {
      pendingQuantity,
      pendingQuantities,
      offeringId,
      isSoldOut,
      isSubscriberExclusive,
      isSubscribed,
      price,
      text,
      useQuantityInput,
    } = this.props;

    const displayQuantity = this.getDisplayQuantity(
      pendingQuantity,
      pendingQuantities,
      offeringId
    );

    let displayText;

    if (text) {
      displayText = text;
    } else if (isSoldOut) {
      displayText = (
        <TranslatedMessage id={'cart.add_to_cart_button.out_of_stock'} />
      );
    } else if (isSubscriberExclusive && !isSubscribed) {
      displayText = (
        <TranslatedMessage
          id={'cart.subscription_commit_button.get_it_with_autoship'}
        />
      );
    } else if (displayQuantity === 0 || !useQuantityInput) {
      const optionallyRenderPrice = () =>
        isFinite(price) ? (
          <>
            {' - '}
            <PriceSpan amount={price} />
          </>
        ) : null;
      displayText = (
        <>
          <TranslatedMessage id={'cart.add_to_cart_button.add_to_cart'} />
          {optionallyRenderPrice()}
        </>
      );
    } else {
      displayText = (
        <TranslatedMessage
          id={'cart.add_to_cart_button.with_quantity'}
          values={{ count: displayQuantity }}
        />
      );
    }

    return displayText;
  }

  redirectToStoreLocator = () => {
    window.location.assign(localisedPath('/locator'));
  };

  render() {
    const {
      buttonComponent,
      isSaving,
      isSoldOut,
      className,
      pendingQuantity,
      pendingQuantities,
      sku,
      variantsSaving,
      retailExclusive,
      offeringId,
      price,
      useQuantityInput,
    } = this.props;

    const isVariantProcessing = sku
      ? variantsSaving.some(item => {
          return item.sku === sku && item.offeringId === offeringId;
        })
      : isSaving;

    const isDisabled =
      isSoldOut ||
      (useQuantityInput &&
        this.getDisplayQuantity(
          pendingQuantity,
          pendingQuantities,
          offeringId
        ) <= 0);

    const ButtonComponent = buttonComponent || Button;
    const buttonClasses = classNames(className, {
      AddToCartButton: ButtonComponent === Button,
    });

    if (retailExclusive) {
      return (
        <ButtonComponent
          onClick={this.redirectToStoreLocator}
          className={buttonClasses}
        >
          <TranslatedMessage id={'cart.add_to_cart_button.find_store'} />
        </ButtonComponent>
      );
    }

    return (
      <ButtonComponent
        onClick={this.handleAddToCartClick}
        processing={isVariantProcessing}
        disabled={isVariantProcessing || isDisabled || isSoldOut}
        className={buttonClasses}
      >
        {this.text}
      </ButtonComponent>
    );
  }
}
